import { useEffect, useMemo, useState, type DragEvent } from "react";
import { P, has } from "@majlis/protocol/permissions";
import type { Channel, InvitePreview, Member } from "@majlis/protocol/types";
import { get, post, patch, ApiError } from "../lib/api.ts";
import { t, useLang } from "../lib/i18n.ts";
import { navigate, useLocation, match } from "../lib/router.ts";
import { useStore, loadMembers, toast, errText, signedOut, upsertServer, serverBadge, homeBadge, totalBadge, isUnread, mentionsIn, isMuted, notifyLevel, updateSettings, dmName, type ServerState } from "../lib/store.ts";
import { HomeSidebar, FriendsPage, DmPanel, NewGroupModal, ProfileModal } from "./Social.tsx";
import { Avatar, Icon, Menu, Confirm, displayName, Spinner, ServerIcon } from "./common.tsx";
import { Login, Register, Forgot, ResetPassword, VerifyEmail, ConfirmEmail, AuthShell } from "./Auth.tsx";
import { ChatView, PinsPanel, SearchPanel } from "./Chat.tsx";
import { VoiceChannelRow, VoiceRoom, VoicePanel, VoiceControls, IncomingCalls, DmCallButton, DmCallStrip } from "./Voice.tsx";
import { CreateServerModal, JoinServerModal, InviteModal, CreateChannelModal, ServerSettings, ChannelSettings, UserSettings } from "./Settings.tsx";

export function App() {
  useLang();
  const { path, search } = useLocation();
  const phase = useStore((s) => s.phase);
  const toastMsg = useStore((s) => s.toast);

  let page: React.ReactNode;
  const vt = search.get("token") ?? "";
  if (path === "/verify-email") page = <VerifyEmail token={vt} signedIn={phase === "ready"} />;
  else if (path === "/reset-password") page = <ResetPassword token={vt} />;
  else if (path === "/confirm-email" && phase !== "boot") page = <ConfirmEmail token={vt} signedIn={phase === "ready"} />;
  else if (phase === "boot") page = <div className="boot"><span className="logo">م</span><Spinner /></div>;
  else if (phase === "anon") {
    const inv = match("/invite/:code", path);
    if (path === "/register") page = <Register next={search.get("next") ?? ""} />;
    else if (path === "/forgot") page = <Forgot />;
    else if (inv) page = <InvitePage code={inv.code!} signedIn={false} />;
    else page = <Login next={path.startsWith("/channels") || path.startsWith("/invite") ? path : search.get("next") ?? ""} />;
  } else {
    const inv = match("/invite/:code", path);
    if (inv) page = <InvitePage code={inv.code!} signedIn />;
    else if (path === "/login" || path === "/register" || path === "/" || path === "/forgot") page = <Redirect to={search.get("next") || "/channels/@me"} />;
    else page = <Shell path={path} search={search} />;
  }
  return <>{page}{toastMsg && <div className="toast" role="status">{toastMsg.text}</div>}</>;
}

function Redirect({ to }: { to: string }) { useEffect(() => navigate(to, true), [to]); return null; }

function InvitePage({ code, signedIn }: { code: string; signedIn: boolean }) {
  const [inv, setInv] = useState<InvitePreview | null | "bad">(null);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  useEffect(() => { get<InvitePreview>(`/invites/${code}`).then(setInv, () => setInv("bad")); }, [code]);
  return (
    <AuthShell>
      <div className="stack center">
        {inv === null ? <Spinner /> : inv === "bad" ? <p className="err">{t("inviteInvalid")}</p> : <>
          <p className="muted">{t("invitedTo")}</p>
          <div className="srv big" aria-hidden="true"><ServerIcon server={inv.server} /></div>
          <h1 dir="auto">{inv.server.name}</h1>
          <p className="muted small">{t("membersCount", { n: inv.server.memberCount })}</p>
          {err && <p className="err">{err}</p>}
          {signedIn
            ? <button className="btn pri block" disabled={busy} onClick={async () => {
                setBusy(true);
                try { const s = await post(`/invites/${code}`); upsertServer(s); navigate(`/channels/${s.id}/${s.channels.find((c: Channel) => c.kind === "text")?.id ?? ""}`, true); }
                catch (e) { setErr(errText(e)); setBusy(false); }
              }}>{t("acceptInvite")}</button>
            : <button className="btn pri block" onClick={() => navigate(`/register?next=${encodeURIComponent(`/invite/${code}`)}`)}>{t("signUp")}</button>}
        </>}
        {signedIn && <a href="/channels/@me" onClick={(e) => { e.preventDefault(); navigate("/channels/@me"); }} className="small">{t("close")}</a>}
      </div>
    </AuthShell>
  );
}

// ---------- shell ----------
function Shell({ path }: { path: string; search: URLSearchParams }) {
  const st = useStore((s) => s);
  const { servers, serverOrder: order, conn } = st;
  const me = st.me!;
  const myStatus = st.myStatus === "invisible" ? "offline" : st.myStatus;
  const params = match("/channels/:sid/:cid", path) ?? match("/channels/:sid", path) ?? {};
  const home = params.sid === "@me";
  const sid = home ? null : params.sid ?? null;
  const server = sid ? servers[sid] : undefined;
  const textChannels = server?.channels.filter((c) => c.kind === "text") ?? [];
  const channel = server ? textChannels.find((c) => c.id === params.cid) : undefined;
  const voiceChannel = server && !channel ? server.channels.find((c) => c.kind === "voice" && c.id === params.cid) : undefined;
  const dm = home && params.cid ? st.dms[params.cid] : undefined;
  const [drawer, setDrawer] = useState(false);
  const [modal, setModal] = useState<null | "create" | "join" | "invite" | "settings" | "user" | "newChannel" | "newCategory" | "newGroup" | { channel: Channel }>(null);
  const [profile, setProfile] = useState<string | null>(null);
  const [panel, setPanel] = useState<"members" | "pins" | "search" | null>(() => (matchMedia("(min-width: 1180px)").matches ? "members" : null));
  const [query, setQuery] = useState("");
  const [jumpTo, setJumpTo] = useState<string | null>(null);

  // Redirect to a sensible channel when the URL points at something gone or missing.
  useEffect(() => {
    if (!home && !sid) { navigate("/channels/@me", true); return; }
    if (sid && !server) { navigate("/channels/@me", true); return; }
    if (home && params.cid && !dm) { navigate("/channels/@me", true); return; }
    if (server && !channel && !voiceChannel && textChannels[0]) navigate(`/channels/${server.id}/${textChannels[0].id}`, true);
  }, [sid, home, params.cid, !!dm, server?.id, channel?.id, voiceChannel?.id, textChannels[0]?.id]);
  useEffect(() => { if (server) loadMembers(server.id).catch(() => {}); }, [server?.id]);
  useEffect(() => { setDrawer(false); setJumpTo(null); }, [params.cid, params.sid]);
  const badge = totalBadge(st);
  const title = channel ? `#${channel.name} · ${server!.name}` : voiceChannel ? `${voiceChannel.name} · ${server!.name}` : dm ? dmName(dm) : t("appName");
  useEffect(() => { document.title = (badge ? `(${badge}) ` : "") + title; }, [title, badge]);

  const muted = (id: string) => isMuted(st, id);
  const toggleMute = (id: string) => updateSettings({ mutedChannels: muted(id) ? { remove: [id] } : { add: [id] } }).catch((e) => toast(errText(e)));
  const header = (icon: string, name: string, topic: string | null, extra: React.ReactNode, mutable: string) => (
    <header className="top">
      <button className="ib menu-btn" onClick={() => setDrawer(true)} aria-label={t("openMenu")}><Icon name="menu" /></button>
      <div className="ttl"><Icon name={icon} size={20} /><b dir="auto">{name}</b>{topic && <small dir="auto">{topic}</small>}</div>
      <span className="sp" />
      {extra}
      <button className="ib" aria-pressed={muted(mutable)} onClick={() => toggleMute(mutable)} aria-label={muted(mutable) ? t("unmuteChannel") : t("muteChannel")} title={muted(mutable) ? t("unmuteChannel") : t("muteChannel")}><Icon name={muted(mutable) ? "bellOff" : "bell"} /></button>
      <button className="ib" aria-pressed={panel === "pins"} onClick={() => setPanel(panel === "pins" ? null : "pins")} aria-label={t("pinned")} title={t("pinned")}><Icon name="pin" /></button>
      <button className="ib" aria-pressed={panel === "members"} onClick={() => setPanel(panel === "members" ? null : "members")} aria-label={t("members")} title={t("members")}><Icon name="users" /></button>
    </header>
  );
  const hb = homeBadge(st);

  return (
    <div className={`app${drawer ? " open" : ""}`}>
      <nav className="rail" aria-label="Servers">
        <button className={`srv home${home ? " on" : ""}`} onClick={() => navigate("/channels/@me")} aria-label={t("appName")}>م{hb > 0 && <span className="dot">{hb}</span>}</button>
        <span className="sep" />
        {order.map((id) => { const s = servers[id]; if (!s) return null; const b = serverBadge(st, id); return (
          <button key={id} className={`srv${id === sid ? " on" : ""}${b.unread && id !== sid ? " unread" : ""}`} title={s.name} aria-label={s.name}
            onClick={() => { const first = s.channels.find((c) => c.kind === "text"); navigate(`/channels/${id}/${first?.id ?? ""}`); }}><ServerIcon server={s} />{b.mentions > 0 && <span className="dot">{b.mentions}</span>}</button>); })}
        <button className="srv add" onClick={() => setModal("create")} aria-label={t("createServer")} title={t("createServer")}><Icon name="plus" /></button>
        <button className="srv add" onClick={() => setModal("join")} aria-label={t("joinServer")} title={t("joinServer")}><Icon name="link" /></button>
      </nav>

      <aside className="rooms">
        {server ? <ServerSidebar server={server} activeId={channel?.id ?? voiceChannel?.id} onModal={setModal} /> : <HomeSidebar activeDm={dm?.id ?? null} onNewGroup={() => setModal("newGroup")} />}
        <VoicePanel />
        <div className="me">
          <Avatar user={me} size={34} status={myStatus} />
          <div className="nm"><b dir="auto">{displayName(me)}</b>{me.customStatus ? <span dir="auto" className="cs">{me.customStatus}</span> : <span dir="ltr">@{me.username}</span>}</div>
          <VoiceControls withLeave={false} />
          <button className="ib sm" onClick={() => setModal("user")} aria-label={t("userSettings")} title={t("userSettings")}><Icon name="settings" size={18} /></button>
        </div>
      </aside>
      <div className="drawer-scrim" onClick={() => setDrawer(false)} />

      <main className="main">
        {conn !== "online" && <div className="conn" role="status">{conn === "reconnecting" ? t("reconnecting") : t("connecting")}</div>}
        {!me.emailVerified && <VerifyBanner />}
        {server && channel ? <>
          {header("hash", channel.name, channel.topic, <label className="search"><Icon name="search" size={16} /><input id="search" placeholder={t("searchPlaceholder")} value={query} dir="auto"
              onChange={(e) => { setQuery(e.target.value); setPanel(e.target.value.trim() ? "search" : panel === "search" ? null : panel); }} /></label>, channel.id)}
          <div className="content">
            <div className="chat"><ChatView server={server} channel={channel} jumpTo={jumpTo} onProfile={setProfile} /></div>
            {panel === "members" && <MemberList server={server} onProfile={setProfile} />}
            {panel === "pins" && <PinsPanel channel={channel} onClose={() => setPanel(null)} />}
            {panel === "search" && query.trim() && <SearchPanel server={server} query={query.trim()} onClose={() => { setPanel(null); setQuery(""); }}
              onJump={(cid, mid) => { setJumpTo(mid); navigate(`/channels/${server.id}/${cid}`); }} />}
          </div>
        </> : voiceChannel && server ? <VoiceRoom title={voiceChannel.name} channelId={voiceChannel.id} serverId={server.id} canConnect={has(BigInt(voiceChannel.permissions), P.CONNECT)} onMenu={() => setDrawer(true)} />
        : dm ? <>
          {header(dm.kind === "dm" ? "at" : "users", dmName(dm), dm.kind === "dm" && dm.recipients[0] ? t(st.presences[dm.recipients[0].id] ?? "offline") : null,
            <DmCallButton channelId={dm.id} canCall={has(BigInt(dm.permissions), P.CONNECT)} />, dm.id)}
          <DmCallStrip channelId={dm.id} title={dmName(dm)} />
          <div className="content">
            <div className="chat"><ChatView server={null} channel={{ id: dm.id, name: dmName(dm), topic: null, permissions: dm.permissions, dm: dm.kind }} jumpTo={null} onProfile={setProfile} /></div>
            {panel === "members" && <DmPanel dm={dm} onProfile={setProfile} />}
            {panel === "pins" && <PinsPanel channel={{ id: dm.id, name: dmName(dm), topic: null, permissions: dm.permissions }} onClose={() => setPanel(null)} />}
          </div>
        </> : home ? <FriendsPage onMenu={() => setDrawer(true)} onProfile={setProfile}
            welcome={order.length === 0 && Object.keys(st.relationships).length === 0 ? <div className="welcome slim">
              <span className="logo xl">م</span><h1>{t("welcomeTitle")}</h1><p className="muted">{t("welcomeBody")}</p>
              <div className="row"><button className="btn pri" onClick={() => setModal("create")}>{t("createServer")}</button><button className="btn ghost" onClick={() => setModal("join")}>{t("joinServer")}</button></div>
            </div> : null} />
          : server ? <div className="welcome"><button className="ib menu-btn" onClick={() => setDrawer(true)} aria-label={t("openMenu")}><Icon name="menu" /></button><p className="muted">{t("noChannels")}</p></div>
          : null}
      </main>

      <IncomingCalls />
      {modal === "create" && <CreateServerModal onClose={() => setModal(null)} />}
      {modal === "join" && <JoinServerModal onClose={() => setModal(null)} />}
      {modal === "user" && <UserSettings onClose={() => setModal(null)} />}
      {modal === "newGroup" && <NewGroupModal onClose={() => setModal(null)} />}
      {profile && <ProfileModal userId={profile} onClose={() => setProfile(null)} />}
      {server && modal === "invite" && <InviteModal server={server} onClose={() => setModal(null)} />}
      {server && modal === "settings" && <ServerSettings server={server} onClose={() => setModal(null)} />}
      {server && (modal === "newChannel" || modal === "newCategory") && <CreateChannelModal server={server} kind={modal === "newChannel" ? "text" : "category"} onClose={() => setModal(null)} />}
      {server && modal && typeof modal === "object" && <ChannelSettings server={server} channel={server.channels.find((c) => c.id === modal.channel.id) ?? modal.channel} onClose={() => setModal(null)} />}
    </div>
  );
}

function VerifyBanner() {
  const [sent, setSent] = useState(false);
  return <div className="banner">{t("verifyBanner")} <button className="lnk" disabled={sent} onClick={() => post("/auth/verify-email/resend").then(() => setSent(true), (e) => toast(errText(e)))}>{sent ? t("sent") : t("resend")}</button></div>;
}

type ModalSetter = (m: any) => void;

function ServerSidebar({ server, activeId, onModal }: { server: ServerState; activeId?: string; onModal: ModalSetter }) {
  const me = useStore((s) => s.me)!;
  const st = useStore((s) => s);
  const level = notifyLevel(st, server.id);
  const [notifyMenu, setNotifyMenu] = useState(false);
  const base = BigInt(server.permissions);
  const [menu, setMenu] = useState(false);
  const [leave, setLeave] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const canManage = has(base, P.MANAGE_CHANNELS);
  const canSettings = [P.MANAGE_SERVER, P.MANAGE_ROLES, P.KICK_MEMBERS, P.BAN_MEMBERS, P.VIEW_AUDIT_LOG].some((b) => has(base, b));
  const groups = useMemo(() => {
    const cats = server.channels.filter((c) => c.kind === "category");
    const leaf = (c: Channel) => c.kind === "text" || c.kind === "voice";
    const loose = server.channels.filter((c) => leaf(c) && (!c.parentId || !cats.some((k) => k.id === c.parentId)));
    return [{ cat: null as Channel | null, items: loose }, ...cats.map((cat) => ({ cat, items: server.channels.filter((c) => leaf(c) && c.parentId === cat.id) }))];
  }, [server.channels]);

  const drop = async (e: DragEvent, targetParent: string | null, beforeId: string | null) => {
    e.preventDefault(); e.stopPropagation();
    const id = dragId; setDragId(null);
    if (!id || id === beforeId) return;
    const siblings = server.channels.filter((c) => (c.kind === "text" || c.kind === "voice") && (c.parentId ?? null) === targetParent && c.id !== id);
    const idx = beforeId ? siblings.findIndex((c) => c.id === beforeId) : siblings.length;
    const ordered = [...siblings.slice(0, idx < 0 ? siblings.length : idx), server.channels.find((c) => c.id === id)!, ...siblings.slice(idx < 0 ? siblings.length : idx)];
    try { await patch(`/servers/${server.id}/channels`, ordered.map((c, i) => ({ id: c.id, position: i, ...(c.id === id ? { parentId: targetParent } : {}) }))); }
    catch (er) { toast(errText(er)); }
  };

  return (
    <>
      <div className="rhead">
        <button className="srvname" onClick={() => setMenu((v) => !v)} aria-haspopup="menu" aria-expanded={menu} aria-label={t("serverMenu")}>
          <h1 dir="auto">{server.name}</h1><Icon name="chevron" size={18} /></button>
        <span className="sub">{t("membersCount", { n: server.memberCount })}</span>
        {menu && <Menu onClose={() => setMenu(false)}>
          {has(base, P.CREATE_INVITE) && <button onClick={() => { setMenu(false); onModal("invite"); }}><Icon name="link" size={18} /> {t("invitePeople")}</button>}
          {canSettings && <button onClick={() => { setMenu(false); onModal("settings"); }}><Icon name="settings" size={18} /> {t("serverSettings")}</button>}
          {canManage && <button onClick={() => { setMenu(false); onModal("newChannel"); }}><Icon name="hash" size={18} /> {t("createChannel")}</button>}
          {canManage && <button onClick={() => { setMenu(false); onModal("newCategory"); }}><Icon name="folder" size={18} /> {t("createCategory")}</button>}
          <button onClick={() => { setMenu(false); setNotifyMenu(true); }}><Icon name="bell" size={18} /> {t("notificationSettings")}</button>
          {server.ownerId !== me.id && <button className="danger" onClick={() => { setMenu(false); setLeave(true); }}><Icon name="logout" size={18} /> {t("leaveServer")}</button>}
        </Menu>}
        {notifyMenu && <Menu onClose={() => setNotifyMenu(false)}>
          {(["all", "mentions", "none"] as const).map((l) => <button key={l} role="menuitemradio" aria-checked={level === l}
            onClick={() => { setNotifyMenu(false); updateSettings({ serverNotify: { [server.id]: l === "mentions" ? null : l } }).catch((e) => toast(errText(e))); }}>
            <Icon name={level === l ? "check" : "dotEmpty"} size={18} /> {t(l === "all" ? "notifyAll" : l === "mentions" ? "notifyMentions" : "notifyNone")}</button>)}
        </Menu>}
      </div>
      <div className="rlist">
        {groups.map(({ cat, items }) => (
          <div key={cat?.id ?? "_"} className="grp" onDragOver={(e) => canManage && dragId && e.preventDefault()} onDrop={(e) => canManage && drop(e, cat?.id ?? null, null)}>
            {cat && <div className="cat"><span dir="auto">{cat.name}</span>
              {has(BigInt(cat.permissions), P.MANAGE_CHANNELS) && <button className="ib xs" onClick={() => onModal({ channel: cat })} aria-label={t("channelSettings")}><Icon name="settings" size={14} /></button>}</div>}
            {items.map((c) => {
              if (c.kind === "voice") return (
                <div key={c.id} className={dragId === c.id ? "dragging" : ""} draggable={canManage}
                  onDragStart={(e) => { setDragId(c.id); e.dataTransfer.effectAllowed = "move"; }} onDragEnd={() => setDragId(null)}
                  onDragOver={(e) => canManage && dragId && e.preventDefault()} onDrop={(e) => canManage && drop(e, cat?.id ?? null, c.id)}>
                  <VoiceChannelRow server={server} channel={c} active={c.id === activeId} onSettings={has(BigInt(c.permissions), P.MANAGE_CHANNELS) ? () => onModal({ channel: c }) : undefined} /></div>);
              const priv = c.overwrites.some((o) => o.targetKind === 0 && server.roles.find((r) => r.isEveryone)?.id === o.targetId && (BigInt(o.deny) & P.VIEW_CHANNEL));
              return (
                <div key={c.id} className={`chrow${dragId === c.id ? " dragging" : ""}`} draggable={canManage}
                  onDragStart={(e) => { setDragId(c.id); e.dataTransfer.effectAllowed = "move"; }} onDragEnd={() => setDragId(null)}
                  onDragOver={(e) => canManage && dragId && e.preventDefault()} onDrop={(e) => canManage && drop(e, cat?.id ?? null, c.id)}>
                  <a className={`ch${isMuted(st, c.id) ? " muted" : level !== "none" && isUnread(st, c.id, c.lastMessageId) ? " unread" : ""}`} href={`/channels/${server.id}/${c.id}`} aria-current={c.id === activeId ? "page" : undefined}
                    onClick={(e) => { e.preventDefault(); navigate(`/channels/${server.id}/${c.id}`); }}>
                    <Icon name={priv ? "lock" : "hash"} size={18} /><span dir="auto">{c.name}</span>
                    {!isMuted(st, c.id) && level !== "none" && mentionsIn(st, c.id) > 0 && <span className="badge">{mentionsIn(st, c.id)}</span>}</a>
                  {has(BigInt(c.permissions), P.MANAGE_CHANNELS) && <button className="ib xs gear" onClick={() => onModal({ channel: c })} aria-label={t("channelSettings")}><Icon name="settings" size={14} /></button>}
                </div>);
            })}
          </div>
        ))}
      </div>
      {leave && <Confirm title={t("leaveServer")} body={t("leaveServerQ", { name: server.name })} confirmLabel={t("leaveServer")} onClose={() => setLeave(false)}
        onConfirm={async () => { await post(`/servers/${server.id}/leave`); navigate("/channels/@me"); }} />}
    </>
  );
}

function MemberList({ server, onProfile }: { server: ServerState; onProfile: (id: string) => void }) {
  const presences = useStore((s) => s.presences);
  if (!server.members) return <aside className="members"><Spinner /></aside>;
  const all = Object.values(server.members);
  const status = (m: Member) => presences[m.user.id] ?? m.status;
  const hoisted = server.roles.filter((r) => r.hoist && !r.isEveryone).sort((a, b) => b.position - a.position);
  const sections: { title: string; list: Member[] }[] = [];
  const used = new Set<string>();
  for (const r of hoisted) {
    const list = all.filter((m) => !used.has(m.user.id) && status(m) !== "offline" && m.roleIds.includes(r.id));
    list.forEach((m) => used.add(m.user.id));
    if (list.length) sections.push({ title: r.name, list });
  }
  const online = all.filter((m) => !used.has(m.user.id) && status(m) !== "offline");
  const offline = all.filter((m) => status(m) === "offline");
  if (online.length) sections.push({ title: t("online"), list: online });
  if (offline.length) sections.push({ title: t("offline"), list: offline });
  const roleColor = (m: Member) => server.roles.filter((r) => m.roleIds.includes(r.id) && r.color).sort((a, b) => b.position - a.position)[0]?.color;
  return (
    <aside className="members" aria-label={t("members")}>
      {sections.map((s) => <div key={s.title}><div className="cat">{s.title} — {s.list.length}</div>
        {s.list.sort((a, b) => displayName(a.user).localeCompare(displayName(b.user))).map((m) => {
          const c = roleColor(m);
          return <button key={m.user.id} className={`mem${status(m) === "offline" ? " off" : ""}`} onClick={() => onProfile(m.user.id)}>
            <Avatar user={m.user} size={32} status={status(m)} />
            <div><b dir="auto" style={c ? { color: `#${c.toString(16).padStart(6, "0")}` } : undefined}>{m.nickname || displayName(m.user)}</b>
              <small>{server.ownerId === m.user.id ? t("owner") : t(status(m))}</small></div></button>;
        })}</div>)}
    </aside>
  );
}

export { ApiError, signedOut };
